'use client'

import React, { useState, useEffect } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { motion, AnimatePresence } from 'framer-motion'
import type { Variants } from 'framer-motion'
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi'
import { useLanguage } from '../../providers/LanguageProvider'
import { X } from 'lucide-react'

type Point = { label: string; labelAr: string; gallery: string[] }

const layoutPattern = [
  {
    span: 'row-span-2',
    height: 'md:h-[365px] lg:h-[487px] xl:h-[542px]',
  },
  {
    span: 'md:col-span-2',
    height: 'h-48 md:h-44 lg:h-60 xl:h-64',
  },
  {
    height: 'md:h-44 lg:h-[230px] xl:h-[250px]',
  },
  {
    span: 'row-span-2',
    height: 'md:h-[365px] lg:h-[477px] xl:h-[535px]',
  },
  {
    height: 'md:h-44 lg:h-[230px] xl:h-[250px]',
  },
  {
    height: 'md:h-44 lg:h-[230px] xl:h-[250px]',
  },
]

const containerVariants: Variants = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.4,
    },
  },
}

const itemVariants: Variants = {
  hidden: { opacity: 0, x: 100 },
  show: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.4, ease: [0.25, 0.1, 0.25, 1] },
  },
}

const galleryCardVariants: Variants = {
  hidden: { opacity: 0, y: 30 },
  show: (i = 1) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.1,
      duration: 0.5,
      ease: [0.25, 0.1, 0.25, 1], // use an easing array, not a string
    },
  }),
}

const MotionImage = motion(Image)

export default function Gallery() {
  const { language } = useLanguage()
  const isArabic = language === 'ar'

  const [selected, setSelected] = useState<string | null>(null)
  const [showPoints, setShowPoints] = useState(false)
  const [pointsComplete, setPointsComplete] = useState(false)
  const [selectedPoint, setSelectedPoint] = useState<Point | null>(null)
  const [galleryPage, setGalleryPage] = useState(0)
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null)

  type GalleryImage = {
    src: string
    alt: string
    altAr: string
    span?: string
    height?: string
    points: {
      label: string
      labelAr: string
      gallery: string[]
    }[]
  }

  const [imageData, setImageData] = useState<GalleryImage[]>([])

  useEffect(() => {
    const fetchGallery = async () => {
      const res = await fetch('/api/fetch-gallery?depth=99')
      const data = await res.json()
      setImageData(data.reverse())
    }

    fetchGallery()
  }, [])

  // const imageData = ImageData

  useEffect(() => {
    if (selected) {
      const timer = setTimeout(() => setShowPoints(true), 600)
      setPointsComplete(false)
      return () => clearTimeout(timer)
    } else {
      setShowPoints(false)
    }
  }, [selected])

  const selectedImage = imageData.find((img) => img.src === selected)

  // Translation texts
  const translations = {
    en: {
      home: 'Home',
      gallery: 'Gallery',
      title: 'Our Facility in Action',
    },
    ar: {
      home: 'الرئيسية',
      gallery: 'المعرض',
      title: 'منشأتنا قيد العمل',
    },
  }

  const t = translations[isArabic ? 'ar' : 'en']
  if (imageData.length === 0)
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    )
  return (
    <div
      className={`hidden md:block min-h-screen bg-white "py-4 md:pt-6 md:pb-14 lg:pt-8 lg:pb-36 xl:pt-12 xl:pb-52 px-4 sm:px-6 md:px-8 lg:px-12 ${isArabic ? 'rtl' : 'ltr'}`}
    >
      <div className="md:max-w-2xl lg:max-w-5xl xl:max-w-[1250px] 2xl:max-w-[1400px] mx-auto">
        <h1 className="text-[1.5rem] md:text-[1.8rem] lg:text-[1.75rem] xl:text-[2rem]  font-normal text-[#414141] mt-16 mb-4">
          {t.title}
        </h1>

        <div className="relative">
          {/* Section 1: Image Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 lg:gap-4 xl:gap-9 auto-rows-max mt-6 lg:mt-8 xl:mt-12">
            {imageData.map((img, index) => {
              const layout = layoutPattern[index % layoutPattern.length]
              const spanClass = layout?.span || ''
              const heightClass = layout?.height

              return (
                <motion.div
                  key={img.src}
                  layoutId={img.src}
                  className={`relative group overflow-hidden ${spanClass} ${heightClass} cursor-pointer`}
                  onClick={() => {
                    setSelected(img.src)
                    setSelectedPoint(null)
                  }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ layout: { duration: 0.4 }, opacity: { duration: 0.2 } }}
                >
                  <Image
                    src={img.src}
                    alt={isArabic ? img.altAr : img.alt}
                    fill
                    className="object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  <div
                    className={`absolute bottom-6 text-white flex items-center gap-1 ${isArabic ? 'right-6' : 'left-6'}`}
                  >
                    <h3 className=" text-xs lg:text-sm">{isArabic ? img.altAr : img.alt}</h3>
                    <FiChevronRight
                      className={`text-white text-lg ${isArabic ? 'rotate-180' : ''}`}
                    />
                  </div>
                </motion.div>
              )
            })}
          </div>

          {/* Section 2: Overlay (Expanded View + Points) */}
          <AnimatePresence>
            {selected && !selectedPoint && (
              <motion.div
                key="expanded"
                layoutId={selected}
                className="absolute inset-0 z-20 bg-black bg-opacity-80"
                onClick={() => setSelected(null)}
                exit={{ opacity: 1 }}
                transition={{
                  layout: {
                    duration: 0.45,
                    ease: [0.25, 0.1, 0.25, 1], // Smooth cubic-bezier
                  },
                  opacity: {
                    duration: 0.35,
                    ease: 'easeInOut',
                  },
                }}
                dir={isArabic ? 'rtl' : 'ltr'}
              >
                {/* Back Button */}
                {selectedPoint == null && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation() // 👈 prevent triggering background click
                      setSelected(null)
                    }}
                    className="absolute top-12 start-8 z-30 text-white text-2xl font-bold flex items-center gap-1"
                  >
                    <FiChevronRight className={`text-xl ${!isArabic ? 'rotate-180' : ''}`} />
                  </button>
                )}

                {/* Fullscreen Background Image */}
                <Image
                  src={selected}
                  alt="Expanded"
                  fill
                  className="object-cover pointer-events-none"
                />

                {/* Alt Title */}
                <div className="absolute bottom-6 start-6 text-white text-sm lg:text-lg font-normal flex items-center gap-1">
                  {isArabic ? selectedImage?.altAr : selectedImage?.alt}
                </div>

                {/* Points */}
                <div className="absolute bottom-6 end-6 w-full z-30">
                  <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    animate="show"
                    className="space-y-8 p-6 rounded-xl"
                    onClick={(e) => e.stopPropagation()} // 👈 prevent clicks on points area from closing
                  >
                    {selectedImage?.points.map((point, index) => (
                      <motion.div
                        key={index}
                        variants={itemVariants}
                        className="flex items-center justify-end text-right"
                        onAnimationComplete={() => {
                          if (index === selectedImage.points.length - 1) {
                            setPointsComplete(true)
                          }
                        }}
                      >
                        <button
                          onClick={(e) => {
                            e.stopPropagation() // 👈 stops propagation from button
                            setSelectedPoint(point)
                          }}
                          className="flex items-center gap-2 text-white text-sm lg:text-xl leading-relaxed border-b border-white pb-2 capitalize"
                        >
                          <span className={isArabic ? 'text-right w-full' : 'text-left w-full'}>
                            {isArabic ? point.labelAr : point.label}
                          </span>
                          {pointsComplete && (
                            <motion.span
                              initial={{ opacity: 0, x: isArabic ? -20 : 20 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ duration: 0.3, delay: index * 0.1 }}
                              className="text-white flex-shrink-0"
                            >
                              {isArabic ? <FiChevronLeft /> : <FiChevronRight />}
                            </motion.span>
                          )}
                        </button>
                      </motion.div>
                    ))}
                  </motion.div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Section 3: Overlay Gallery for Selected Point */}
          <AnimatePresence>
            {selectedPoint && (
              <motion.div
                key="gallery"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 z-30 bg-black bg-opacity-95 px-28 pt-24 overflow-y-auto"
              >
                {/* Close Button */}
                {selectedImageIndex == null && (
                  <button
                    onClick={() => setSelectedPoint(null)}
                    className={`absolute top-12 flex items-center gap-1 text-white text-base z-40 ${isArabic ? 'right-8' : 'left-8'}`}
                  >
                    <FiChevronRight className={`text-2xl ${isArabic ? '' : 'rotate-180'}`} />
                  </button>
                )}

                {/* Gallery Wrapper with Arrows */}
                <div className="relative">
                  {/* Left Arrow */}
                  {selectedImageIndex === null && selectedPoint.gallery.length > 9 && (
                    <button
                      onClick={() => setGalleryPage((prev) => Math.max(prev - 1, 0))}
                      disabled={galleryPage === 0}
                      className={`absolute top-1/2 -translate-y-1/2 text-white text-xl px-4 z-40 disabled:opacity-30 ${isArabic ? '-right-16' : '-left-16'}`}
                    >
                      <FiChevronLeft className={isArabic ? 'rotate-180' : ''} />
                    </button>
                  )}

                  {/* Gallery Grid */}
                  <div
                    className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 transition-all"
                    style={{ minHeight: '550px' }}
                  >
                    {selectedImageIndex === null ? (
                      // Show normal grid
                      selectedPoint.gallery
                        .slice(galleryPage * 9, (galleryPage + 1) * 9)
                        .map((src, index) => {
                          const actualIndex = index + galleryPage * 9
                          return (
                            <motion.div
                              key={index}
                              className="relative h-44 overflow-hidden cursor-pointer"
                              onClick={() => setSelectedImageIndex(actualIndex)}
                              initial="hidden"
                              animate="show"
                              variants={galleryCardVariants}
                            >
                              <MotionImage
                                src={src}
                                alt={`${isArabic ? selectedPoint.labelAr : selectedPoint.label} ${index + 1}`}
                                fill
                                className="object-cover"
                                initial={{ scale: 1 }}
                                whileHover={{ scale: 1.1 }}
                                transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
                              />
                            </motion.div>
                          )
                        })
                    ) : (
                      // Show only selected image, full width
                      <motion.div
                        key="expanded-image"
                        layout
                        className="relative col-span-full h-[550px]  rounded-md cursor-pointer"
                        onClick={() => setSelectedImageIndex(null)}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                      >
                        <MotionImage
                          src={selectedPoint.gallery[selectedImageIndex]}
                          alt="Expanded Image"
                          fill
                          className="object-contain"
                          initial={{ scale: 0.95 }}
                          animate={{ scale: 1 }}
                          transition={{ duration: 0.5 }}
                        />

                        {/* Left Arrow */}
                        {selectedImageIndex > 0 && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation() // Prevent closing overlay
                              setSelectedImageIndex((prev) => (prev !== null ? prev - 1 : null))
                            }}
                            className={`absolute top-1/2 -translate-y-1/2 text-white text-2xl px-4 z-50 ${isArabic ? 'md:-right-16 2xl:right-0' : 'md:-left-16 2xl:left-0'}`}
                          >
                            <FiChevronLeft className={isArabic ? 'rotate-180' : ''} />
                          </button>
                        )}

                        {/* Right Arrow */}
                        {selectedImageIndex < selectedPoint.gallery.length - 1 && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              setSelectedImageIndex((prev) => (prev !== null ? prev + 1 : null))
                            }}
                            className={`absolute top-1/2 -translate-y-1/2 text-white text-2xl px-4 z-50 ${isArabic ? 'md:-left-16 2xl:left-0' : 'md:-right-16 2xl:right-0'}`}
                          >
                            <FiChevronRight className={isArabic ? 'rotate-180' : ''} />
                          </button>
                        )}

                        {/* Close button */}
                        <button
                          onClick={() => setSelectedImageIndex(null)}
                          className="absolute  right-3 z-10 bg-black bg-opacity-60 text-white p-1 rounded hover:bg-opacity-80 transition"
                          aria-label="Close"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </motion.div>
                    )}
                  </div>

                  {/* Right Arrow */}
                  {selectedImageIndex === null && selectedPoint.gallery.length > 9 && (
                    <button
                      onClick={() =>
                        setGalleryPage((prev) =>
                          selectedPoint
                            ? Math.min(prev + 1, Math.floor(selectedPoint.gallery.length / 9))
                            : prev,
                        )
                      }
                      disabled={
                        !selectedPoint ||
                        galleryPage >= Math.floor(selectedPoint.gallery.length / 9)
                      }
                      className={`absolute top-1/2 -translate-y-1/2 text-white text-xl px-4 z-40 disabled:opacity-30 ${isArabic ? '-left-16' : '-right-16'}`}
                    >
                      <FiChevronRight className={isArabic ? 'rotate-180' : ''} />
                    </button>
                  )}
                </div>

                {/* Pagination Dots */}
                <div className="flex justify-center mt-6 space-x-2 rtl:space-x-reverse">
                  {Array.from({
                    length: selectedPoint ? Math.ceil(selectedPoint.gallery.length / 9) : 0,
                  }).map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setGalleryPage(i)}
                      className={`w-2 h-2 rounded-full ${
                        i === galleryPage ? 'bg-white' : 'bg-gray-500'
                      }`}
                    />
                  ))}
                </div>

                {/* Labels Below Dots */}
                <div
                  className={`relative h-6 text-white text-sm lg:text-base mb-2 mt-4 xl:mt-16 ${isArabic ? 'text-right' : ''}`}
                >
                  {/* Left/Right: alt text */}
                  <div className={`absolute font-normal ${isArabic ? '-right-16' : '-left-16'}`}>
                    {isArabic ? selectedImage?.altAr : selectedImage?.alt}
                  </div>

                  {/* Right/Left: label text */}
                  <div className={`font-normal underline ${isArabic ? 'text-left' : 'text-right'}`}>
                    {isArabic ? selectedPoint?.labelAr : selectedPoint?.label}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  )
}
